import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, AlertTriangle } from 'lucide-react';

const documentCategories = [
  {
    id: 'fourth-amendment',
    title: '4th Amendment',
    description: 'Documents related to Fourth Amendment rights and protections against unreasonable searches and seizures',
    documents: [
      {
        name: 'Know Your Rights Card',
        type: 'PDF Template',
        size: '1.2 MB',
        description: 'Pocket-sized card outlining 4th Amendment rights during police encounters'
      },
      {
        name: 'Search Warrant Challenge Form',
        type: 'DOCX Template',
        size: '1.8 MB',
        description: 'Template for challenging invalid search warrants'
      },
      {
        name: 'Privacy Protection Notice',
        type: 'PDF Template',
        size: '1.1 MB',
        description: 'Notice asserting 4th Amendment privacy protections'
      }
    ]
  },
  {
    id: 'constitutional-processes',
    title: 'Constitutional Processes',
    description: 'Documents for constitutional status correction and related processes',
    documents: [
      {
        name: 'Affidavit of Status Correction',
        type: 'PDF Template',
        size: '2.1 MB',
        description: 'Template for correcting political status'
      },
      {
        name: 'Declaration of Independence',
        type: 'DOCX Template',
        size: '1.8 MB',
        description: 'Personal declaration template'
      },
      {
        name: 'Notice of Status Change',
        type: 'PDF Template',
        size: '1.5 MB',
        description: 'Formal notice to government entities'
      }
    ]
  },
  {
    id: 'trust-estate',
    title: 'Trust and Estate Documents',
    description: 'Templates for establishing trusts and estate planning',
    documents: [
      {
        name: 'Private Trust Agreement',
        type: 'DOCX Template',
        size: '3.2 MB',
        description: 'Comprehensive trust establishment document'
      },
      {
        name: 'Estate Planning Checklist',
        type: 'PDF Guide',
        size: '800 KB',
        description: 'Step-by-step estate planning process'
      },
      {
        name: 'Trustee Instructions',
        type: 'PDF Guide',
        size: '1.2 MB',
        description: 'Guidelines for trustee responsibilities'
      }
    ]
  },
  {
    id: 'banking-finance',
    title: 'Banking and Finance',
    description: 'Documents for financial and banking matters',
    documents: [
      {
        name: 'Foreign Trust Banking Forms',
        type: 'PDF Template',
        size: '2.8 MB',
        description: 'Forms for foreign trust banking setup'
      },
      {
        name: 'EIN Application Template',
        type: 'DOCX Template',
        size: '900 KB',
        description: 'Template for EIN applications'
      },
      {
        name: 'Banking Correspondence',
        type: 'DOCX Template',
        size: '1.1 MB',
        description: 'Sample correspondence with financial institutions'
      }
    ]
  },
  {
    id: 'legal-forms',
    title: 'Legal Forms and Notices',
    description: 'Various legal forms and official notices',
    documents: [
      {
        name: 'Cease and Desist Template',
        type: 'DOCX Template',
        size: '1.4 MB',
        description: 'Template for cease and desist notices'
      },
      {
        name: 'Administrative Notice',
        type: 'PDF Template',
        size: '1.7 MB',
        description: 'Administrative notice template'
      },
      {
        name: 'Request for Information',
        type: 'DOCX Template',
        size: '1.0 MB',
        description: 'FOIA and information request forms'
      }
    ]
  },
  {
    id: 'books-educational',
    title: 'Books and Educational Docs',
    description: 'Educational books and documents for legal understanding',
    documents: [
      {
        name: 'Creditors and their Bonds',
        type: 'PDF Book',
        size: '4.2 MB',
        description: 'Comprehensive guide on creditor relationships and bonding'
      },
      {
        name: 'Operating Circular 10',
        type: 'PDF Book',
        size: '3.8 MB',
        description: 'Essential operating procedures and guidelines'
      },
      {
        name: 'Weiss Trustee Handbook',
        type: 'PDF Book',
        size: '5.1 MB',
        description: 'Complete handbook for trustee operations and responsibilities'
      },
      {
        name: 'Identity Theft and your Social Security Number',
        type: 'PDF Document',
        size: '2.3 MB',
        description: 'Guide to protecting your identity and understanding SSN issues'
      }
    ]
  }
];

const DocumentsTemplates = () => {
  const handleDownload = (documentName: string) => {
    // Handle specific document downloads
    if (documentName === 'Operating Circular 10') {
      window.open('https://drive.google.com/file/d/1mBIPd2Z6Odz0ZLWujT2q6ujGTLT7YX4y/view?usp=sharing', '_blank');
    } else {
      // Placeholder for other documents
      console.log(`Downloading ${documentName}`);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-32 pb-20 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Documents and Templates
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Access our collection of legal documents, templates, and forms to support your constitutional processes and legal independence journey.
            </p>
            
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 max-w-4xl mx-auto mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-destructive mt-1 flex-shrink-0" />
                <div className="text-left">
                  <h3 className="font-semibold text-destructive mb-2">Important Disclaimer</h3>
                  <p className="text-sm text-muted-foreground">
                    All documents and templates must be edited and used at your own risk. Any questions about how these documents work will be considered a request for consultation. The documents are free - our time is not. Please be respectful.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-12">
            {documentCategories.map((category) => (
              <div key={category.id}>
                <div className="mb-8">
                  <h2 className="font-rockwell text-2xl md:text-3xl font-semibold text-primary mb-3">
                    {category.title}
                  </h2>
                  <p className="text-muted-foreground">
                    {category.description}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.documents.map((document, index) => (
                    <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-4">
                          <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                            <FileText className="h-6 w-6 text-primary" />
                          </div>
                          <div className="text-right">
                            <Badge variant="secondary" className="text-xs">
                              {document.type}
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-1">
                              {document.size}
                            </p>
                          </div>
                        </div>
                        <CardTitle className="text-lg group-hover:text-primary transition-colors">
                          {document.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <CardDescription className="mb-4">
                          {document.description}
                        </CardDescription>
                        <Button 
                          className="w-full"
                          onClick={() => handleDownload(document.name)}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="bg-muted/50 border border-gold/10 rounded-lg p-8 max-w-4xl mx-auto">
              <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                Need Help with These Documents?
              </h3>
              <p className="text-muted-foreground mb-6">
                If you need assistance understanding or properly using these documents, consider booking a consultation with our team for personalized guidance.
              </p>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/phone-consultation'}
              >
                Book Consultation
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DocumentsTemplates;